/**
 * Pure helper functions for calendar business logic (no I/O)
 */

import type { PynRoomType, PynPlan, PynReservation } from '@/types/pyn-api';
import type { CalendarDay } from '@/types/calendar';
import { requiredMinStay } from '@/lib/calendar-utils';

/**
 * Minimal festive-plan shape needed to flag festive days. The target window is
 * OPTIONAL (nullable) — a festive plan is identified by its `is_festive` flag,
 * not by having dates, so the highlight works on PYN data alone when no window
 * is set. When a window IS set it acts as an extra signal.
 */
export interface FestivePlanWindow {
  pynPlanId: string;
  targetCheckin: string | null; // YYYY-MM-DD or null
  targetCheckout: string | null; // YYYY-MM-DD or null
}

/**
 * Format date as YYYY-MM-DD in local timezone (not UTC)
 * Using toISOString() causes timezone shift issues
 */
function formatDateLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get all valid rate plans for a room type
 * - Filters plans where HavePrice === 1 AND StandardPrices?.Days exists
 * - Sorted by MediumPrice ascending (cheapest first)
 */
export function getAllValidPlans(roomTypeData: PynRoomType): PynPlan[] {
  const plans = Object.values(roomTypeData.Plans || {});

  const validPlans = plans.filter(
    (plan) =>
      plan.HavePrice === 1 &&
      plan.StandardPrices?.Days &&
      Object.keys(plan.StandardPrices.Days).length > 0
  );

  validPlans.sort((a, b) => {
    const priceA = a.StandardPrices?.MediumPrice ?? Infinity;
    const priceB = b.StandardPrices?.MediumPrice ?? Infinity;
    return priceA - priceB;
  });

  return validPlans;
}

/**
 * Get cheapest rate plan for a room type
 * - Filters plans where HavePrice === 1 AND StandardPrices?.Days exists
 * - Sorts by StandardPrices.MediumPrice ascending
 * - Returns first (cheapest) or null
 */
export function getCheapestValidPlan(roomTypeData: PynRoomType): PynPlan | null {
  const plans = getAllValidPlans(roomTypeData);
  return plans[0] || null;
}

/**
 * Build set of blocked dates from reservations (excluding checkout dates)
 * - Filters reservations by roomId === pmsRoomNumber
 * - Adds dates from checkIn to day BEFORE checkOut (checkout day is available)
 */
export function buildBlockedDatesSet(
  reservations: PynReservation[],
  pmsRoomNumber: string
): Set<string> {
  const blockedDates = new Set<string>();

  const roomReservations = reservations.filter(
    (res) => res.roomId === pmsRoomNumber
  );

  roomReservations.forEach((reservation) => {
    const checkIn = new Date(reservation.checkInDate);
    const checkOut = new Date(reservation.checkOutDate);

    // Block from check-in to day before checkout (checkout day is available)
    for (
      let d = new Date(checkIn);
      d < checkOut;
      d.setDate(d.getDate() + 1)
    ) {
      const dateStr = formatDateLocal(d);
      blockedDates.add(dateStr);
    }
  });

  return blockedDates;
}

/**
 * Compute the (year, month) immediately after the given one. Months are
 * 1-indexed. Pure — lives here (not in calendar-service) so the cache warmer
 * can walk the forward-month window without a static import cycle (doc 85).
 */
export function nextYearMonth(year: number, month: number): { year: number; month: number } {
  return month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 };
}

/**
 * Get the PYN query window for a given month.
 *
 * The reservation window is exactly the month's NIGHTS: `startDate` = the 1st
 * (or today, if later — PMS rejects past dates) through `endDate` = the 1st of
 * the next month, EXCLUSIVE. `days` (≤ 31) is that night count.
 *
 * No lead-in/lead-out padding: PMS `/reservation/search/` returns every
 * reservation OVERLAPPING the window (a Nov 1 query returns an Oct 28
 * check-in), so a stay spilling in from the previous month is already
 * included. The old "5 days before + 5 days after" padding pushed the window
 * past PMS's 31-day cap; the clamp then silently cut the month's LAST days, so
 * any reservation starting there (manual, OTA) was invisible and the room
 * showed free — e.g. every Nov 28 arrival on the Dec 1 long weekend, and the
 * Dec 30 New Year's arrivals. Probed live 2026-10-24.
 */
export function getQueryDateRange(
  year: number,
  month: number
): { startDate: string; endDate: string; checkin: string; checkout: string; days: number } {
  const monthStart = new Date(year, month - 1, 1); // month is 1-indexed
  const nextMonthStart = new Date(year, month, 1);

  // PYN reservation API rejects past dates — clamp start to today
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = monthStart < today ? today : monthStart;

  // Round, not ceil: a DST change inside the window makes the local-midnight
  // span 23h/25h short/long. Minimum 1 so a past month never sends `days=0`.
  const days = Math.max(1, Math.round(
    (nextMonthStart.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
  ));

  // For Hotel Info API: checkin is first day of month, checkout is first day of next month
  const checkin = formatDateLocal(monthStart);
  const checkout = formatDateLocal(nextMonthStart);

  return {
    startDate: formatDateLocal(start),
    endDate: checkout,
    checkin,
    checkout,
    days,
  };
}

/**
 * Per-day plan affordance (doc 81 §3). Returns this plan's price on `date`
 * when the plan is open AND priced there, otherwise null. A plan is "open
 * with price" on a date when:
 *   - it advertises a price (HavePrice === 1 — defensive; callers usually
 *     pass `getAllValidPlans` output which already filters this),
 *   - it is not flagged Closed on that date,
 *   - and `StandardPrices.Days[date]` is a finite number > 0.
 *
 * This is the single source of truth for "is plan P bookable on day D",
 * shared by the calendar per-day winner and the checkout range filter so the
 * two can never drift.
 */
export function planDayPrice(plan: PynPlan, date: string): number | null {
  if (plan.HavePrice !== 1) return null;
  if ((plan.Closed || {})[date] === '1') return null;
  const price = plan.StandardPrices?.Days?.[date];
  if (typeof price !== 'number' || !Number.isFinite(price) || price <= 0) {
    return null;
  }
  return price;
}

/**
 * No-leapfrog range check (D23). True iff THIS single plan is open with a
 * price on every night in the range — i.e. the plan alone can carry the
 * whole stay. `stayNights` must be check-in inclusive, check-out EXCLUSIVE
 * (this is exactly what `getDatesBetween(checkIn, checkOut)` returns, so do
 * not slice it again at call sites).
 */
export function planCoversRange(plan: PynPlan, stayNights: string[]): boolean {
  if (stayNights.length === 0) return false;
  return stayNights.every((date) => planDayPrice(plan, date) !== null);
}

/**
 * The minimum-stay value this plan carries on the night of `date` (doc 81
 * §11.3/§14). Reads `plan.MinStays[date]` and floors at 1. Plans/dates without
 * an entry ⇒ 1. A malformed / non-numeric entry also degrades to 1 (never
 * blocks a stay on bad data).
 *
 * The value belongs to a NIGHT, not to an arrival: PYN applies it to every
 * stay that includes that night (see `planRequiredMinStay`).
 */
export function planMinStay(plan: PynPlan, date: string): number {
  const raw = plan.MinStays?.[date];
  const n = raw === undefined ? 1 : Number(raw);
  return Number.isFinite(n) && n >= 1 ? n : 1;
}

/**
 * Minimum length a stay over `stayNights` must have under THIS plan: the
 * largest `MinStays` value on ANY night of the stay (doc 81 §14). This is
 * PYN's own rule — probed live 2026-10-24: `/hotel/info/` flags a plan
 * `NoValidFor: ["MinStays"]` for Jan 9→11 when only Jan 10 carries a minimum,
 * and for Dec 29→31 when only Dec 30 does, while Jan 12→15 (3 nights over
 * three min-3 nights) passes. The arrival-only reading (`MinStays[checkIn]`)
 * let a Sat→Mon stay through a min-3 Sunday through.
 *
 * `stayNights` must be check-out EXCLUSIVE (`getDatesBetween` output) — the
 * departure day is not a night and its minimum never applies. Empty ⇒ 1.
 */
export function planRequiredMinStay(plan: PynPlan, stayNights: string[]): number {
  return stayNights.reduce((max, date) => Math.max(max, planMinStay(plan, date)), 1);
}

/**
 * Per-plan min-stay gate (doc 81 §14). True iff a stay over `stayNights`
 * (check-out exclusive) satisfies THIS plan's minimum on every night.
 * Authoritative server-side check at checkout, paired with `planCoversRange`.
 */
export function planMeetsMinStay(plan: PynPlan, stayNights: string[]): boolean {
  return stayNights.length >= planRequiredMinStay(plan, stayNights);
}

/**
 * Plans that could carry this stay but for its length: open + priced on every
 * night (`planCoversRange`) yet short of their minimum (`planMeetsMinStay`).
 * Each comes with the nights it requires. Lets checkout tell a guest WHY a
 * rate they asked for (e.g. from an offer page) is missing, instead of
 * silently falling back to another plan. `stayNights` is check-out exclusive.
 */
export function minStayShortfallPlans(
  plans: PynPlan[],
  stayNights: string[]
): Array<{ plan: PynPlan; minStayNights: number }> {
  return plans
    .filter((p) => planCoversRange(p, stayNights) && !planMeetsMinStay(p, stayNights))
    .map((p) => ({ plan: p, minStayNights: planRequiredMinStay(p, stayNights) }));
}

/**
 * No-leapfrog range check (D23, doc 81 §4.4) extended with min-stay (§14). A
 * range is bookable iff ≥1 single plan is open on EVERY night AND the stay
 * length meets the largest aggregate min-stay of ANY night in it (not just the
 * arrival day's — PYN applies a night's minimum to every stay including it).
 * `nights` must be check-out EXCLUSIVE (`getDatesBetween` output) — do NOT
 * slice it again. A missing/hard-unavailable night ⇒ not bookable.
 */
export function rangeBookable(dayMap: Map<string, CalendarDay>, nights: string[]): boolean {
  if (nights.length === 0) return false;
  const perNightSets: Set<string>[] = [];
  for (const date of nights) {
    const day = dayMap.get(date);
    if (!day || !day.available) return false;
    perNightSets.push(new Set(day.openPlanIds));
  }
  // Min-stay (§14): the stay must reach the minimum of every night it covers.
  if (nights.length < requiredMinStay(nights, (d) => dayMap.get(d)?.minStay)) return false;
  // Intersect open-plan sets across all nights: ≥1 survivor ⇒ a single plan
  // spans the whole range.
  const [first, ...rest] = perNightSets;
  return [...first].some((id) => rest.every((s) => s.has(id)));
}

/**
 * Best-rate ("green / cheaper") computation for a rendered month.
 *
 * Considers only bookable, non-past, priced days — AND deliberately EXCLUDES
 * festive days (`isFestive`). A festive package (e.g. plan 14's Jan 12–14) is
 * usually the priciest thing in the month and is often the only open plan on
 * its core dates, so leaving it in would inflate `max` and flip
 * `hasPriceVariation` true even when every ordinary day is the same price —
 * making the whole rest of the month light up green as "cheaper" purely
 * because a holiday is expensive. Festive days render gold (not green) anyway,
 * so dropping them here costs nothing and measures variation among the
 * ordinary days only. `hasPriceVariation` is false (⇒ no green at all) when
 * fewer than two distinct non-festive prices exist.
 */
export function computeBestRate(days: CalendarDay[]): {
  bestRatePrice: number | null;
  hasPriceVariation: boolean;
} {
  const prices = days
    .filter((d) => d.available && !d.isPast && d.price !== null && !d.isFestive)
    .map((d) => d.price as number);
  if (prices.length === 0) return { bestRatePrice: null, hasPriceVariation: false };
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return { bestRatePrice: min, hasPriceVariation: min !== max };
}

/**
 * Compute the minimum daily price advertised across ALL given rate plans for
 * non-past, non-closed, priced dates. Replaces the old per-plan
 * `computeMinPriceFromPlan`: the "starting from" figure is now the cheapest
 * open price any plan offers on any future date, matching the per-day winner
 * model (doc 81 §3). Independent of per-day availability/reservations — the
 * rate-card price stays correct even when the window is fully booked.
 */
export function computeMinPriceAcrossPlans(plans: PynPlan[]): number | null {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = formatDateLocal(today);

  const prices: number[] = [];
  for (const plan of plans) {
    const days = plan.StandardPrices?.Days || {};
    for (const date of Object.keys(days)) {
      if (date < todayStr) continue;
      const price = planDayPrice(plan, date);
      if (price !== null) prices.push(price);
    }
  }
  return prices.length > 0 ? Math.min(...prices) : null;
}

/**
 * Generate calendar days using the per-day winner model (doc 81 §3).
 *
 * For each day we evaluate every valid plan: the day's price is the cheapest
 * open plan's price, and `openPlanIds` lists all plans open that day. The day
 * is bookable when it isn't reserved, the room type has availability, AND at
 * least one plan is open (candidates non-empty). The calendar UI stays
 * plan-agnostic (D24) — no single "selected plan" drives the month.
 *
 * `festivePlans` flags festive days (union hybrid): a day is festive when one
 * of those plans' PYN ids is open+priced that day (PYN = source of truth) OR
 * the day falls inside that plan's CMS `targetCheckin..targetCheckout` window
 * when one is set (inclusive; an optional nice-to-have that survives PYN cache
 * gaps). The window is OPTIONAL — a festive plan with no dates still lights up
 * via PYN alone. Empty list ⇒ no day is ever flagged.
 */
export function generateCalendarDays(params: {
  year: number;
  month: number;
  blockedDates: Set<string>;
  availabilities: Record<string, string>;
  plans: PynPlan[];
  festivePlans?: FestivePlanWindow[];
}): CalendarDay[] {
  const { year, month, blockedDates, availabilities, plans, festivePlans = [] } = params;

  const calendar: CalendarDay[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = formatDateLocal(today);

  // Get first and last day of month
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0); // Last day of the month

  for (
    let d = new Date(firstDay);
    d <= lastDay;
    d.setDate(d.getDate() + 1)
  ) {
    const dateStr = formatDateLocal(d);

    // Per-day winner: collect every plan open+priced on this date. The
    // aggregate min-stay is the smallest min any OPEN plan carries on this
    // night (floored at 1) — a night with any 1-night plan open stays
    // 1-night-bookable. It applies to every stay COVERING this night, not only
    // to arrivals here (doc 81 §14; see `requiredMinStay`).
    const openPlanIds: string[] = [];
    let bestPrice: number | null = null;
    let minStay = 1;
    for (const plan of plans) {
      const price = planDayPrice(plan, dateStr);
      if (price === null) continue;
      const wasEmpty = openPlanIds.length === 0;
      const planMin = planMinStay(plan, dateStr);
      minStay = wasEmpty ? planMin : Math.min(minStay, planMin);
      openPlanIds.push(plan.ID);
      if (bestPrice === null || price < bestPrice) bestPrice = price;
    }

    // Check 1: Is this room reserved?
    const hasReservation = blockedDates.has(dateStr);

    // Check 2: Is this room type available in booking engine?
    // Missing dates = zero availability
    const availCount = availabilities[dateStr];
    const roomTypeAvailable =
      availCount !== undefined && parseInt(availCount, 10) > 0;

    // Available if: no reservation AND room type available AND ≥1 plan open.
    const isAvailable =
      !hasReservation && roomTypeAvailable && openPlanIds.length > 0;

    const isPast = d < today;
    const isToday = dateStr === todayStr;

    // Festive flag (union hybrid): PYN open for a festive plan (source of
    // truth) OR — only when a window is configured — inside its declared CMS
    // window (inclusive of both endpoints, a visual band).
    const isFestive = festivePlans.some(
      (fp) =>
        openPlanIds.includes(fp.pynPlanId) ||
        (fp.targetCheckin !== null &&
          fp.targetCheckout !== null &&
          dateStr >= fp.targetCheckin &&
          dateStr <= fp.targetCheckout)
    );

    calendar.push({
      date: dateStr,
      available: isAvailable,
      // Cheapest open plan's rate-card price for the day, independent of
      // availability (price shows even on greyed/booked days).
      price: bestPrice,
      isPast,
      isToday,
      openPlanIds,
      isFestive,
      // No open plan ⇒ minStay stays 1 (irrelevant; day is unavailable).
      minStay,
    });
  }

  return calendar;
}
